import { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';

import { FlexColumn } from 'ui-components/components/flex/column';
import { Gap } from 'ui-components/components/flex/gap';
import { Input, version2 } from 'ui-components/components/input';
import { INLINE_MESSAGE_TYPES } from 'ui-components/components/inline-message';
import { LabelWithRequiredIndicator } from 'ui-components/components/label-with-required-indicator';

import type { ValidationError } from '../../../../validation/validation-error';
import { messages } from './messages';

type UserInfoFormProps = {
  contactFullName: string;
  companyName: string;
  email: string;
  phoneNumber: string;
  errors?: Record<string, ValidationError>;
  showAllErrors?: boolean;
  isDisabled?: boolean;
  onChange: (field: string, value: string) => void;
  onBlur: (field: string) => void;
};

export const UserInfoForm = ({
  contactFullName,
  companyName,
  email,
  phoneNumber,
  errors,
  showAllErrors = false,
  isDisabled = false,
  onChange,
  onBlur,
}: UserInfoFormProps) => {
  const intl = useIntl();
  const [touchedFields, setTouchedFields] = useState<Record<string, boolean>>({});

  const handleInputChange = useCallback(
    (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
      onChange(field, event.target.value);
    },
    [onChange],
  );

  const handleInputBlur = useCallback(
    (field: string) => () => {
      setTouchedFields((prev) => ({ ...prev, [field]: true }));
      onBlur(field);
    },
    [onBlur],
  );

  const getFieldError = useCallback(
    (field: string) => {
      const shouldShowError = showAllErrors || touchedFields[field];
      const error = shouldShowError ? errors?.[field] : null;
      const inlineMessage = error?.message;
      const inlineMessageType = inlineMessage ? INLINE_MESSAGE_TYPES.error : undefined;
      return { inlineMessage, inlineMessageType };
    },
    [errors, showAllErrors, touchedFields],
  );

  return (
    <FlexColumn gap={Gap.Space8}>
      <Input
        ariaLabel={intl.formatMessage(messages.contactFullNameLabel)}
        disabled={isDisabled}
        id='wwex-contact-full-name'
        inlineMessage={getFieldError('contactFullName').inlineMessage}
        inlineMessageType={getFieldError('contactFullName').inlineMessageType}
        label={<LabelWithRequiredIndicator>{intl.formatMessage(messages.contactFullNameLabel)}</LabelWithRequiredIndicator>}
        onBlur={handleInputBlur('contactFullName')}
        onChange={handleInputChange('contactFullName')}
        value={contactFullName}
        version={version2}
      />

      <Input
        ariaLabel={intl.formatMessage(messages.companyNameLabel)}
        disabled={isDisabled}
        id='wwex-company-name'
        inlineMessage={getFieldError('companyName').inlineMessage}
        inlineMessageType={getFieldError('companyName').inlineMessageType}
        label={<LabelWithRequiredIndicator>{intl.formatMessage(messages.companyNameLabel)}</LabelWithRequiredIndicator>}
        onBlur={handleInputBlur('companyName')}
        onChange={handleInputChange('companyName')}
        value={companyName}
        version={version2}
      />

      <Input
        ariaLabel={intl.formatMessage(messages.emailLabel)}
        disabled={isDisabled}
        id='wwex-email'
        inlineMessage={getFieldError('email').inlineMessage}
        inlineMessageType={getFieldError('email').inlineMessageType}
        label={<LabelWithRequiredIndicator>{intl.formatMessage(messages.emailLabel)}</LabelWithRequiredIndicator>}
        onBlur={handleInputBlur('email')}
        onChange={handleInputChange('email')}
        type='email'
        value={email}
        version={version2}
      />

      <Input
        ariaLabel={intl.formatMessage(messages.phoneNumberLabel)}
        disabled={isDisabled}
        id='wwex-phone-number'
        inlineMessage={getFieldError('phoneNumber').inlineMessage}
        inlineMessageType={getFieldError('phoneNumber').inlineMessageType}
        label={<LabelWithRequiredIndicator>{intl.formatMessage(messages.phoneNumberLabel)}</LabelWithRequiredIndicator>}
        onBlur={handleInputBlur('phoneNumber')}
        onChange={handleInputChange('phoneNumber')}
        type='tel'
        value={phoneNumber}
        version={version2}
      />
    </FlexColumn>
  );
};
